import React from 'react';
import { RiFileTextLine } from 'react-icons/ri';
import { Bubble, AgentChip } from './tutorialHelpers';

const SWARM_TOPIC =
  "Should AI be used to assist in criminal sentencing decisions?";

const SWARM_SUMMARY =
  "Synthesis: The debate surfaced a core tension between consistency " +
  "(AI can reduce judge-to-judge variability) and fairness (training data " +
  "encodes historical bias). The Mediator's advisory framing — with mandatory " +
  "transparency and disclosure to defendants — represents the emerging consensus. " +
  "Key open question: Who audits the algorithms when AI recommendations " +
  "become de-facto standards?";

const SWARM_AGENTS = [
  {
    name: "Analyst",
    color: "#636cff",
    text:
      "Calibrated models could reduce documented sentencing variability. " +
      "Research shows outcomes shift based on time of day, case sequencing, " +
      "and demographic factors unrelated to the crime. A consistent AI " +
      "baseline could be more equitable than the status quo.",
  },
  {
    name: "Skeptic",
    color: "#e53935",
    text:
      "Historical conviction data encodes systemic bias. Any model trained " +
      "on it will reproduce — and lend algorithmic authority to — existing " +
      "racial and socioeconomic disparities, making them harder to challenge " +
      "than a judge's stated reasoning.",
  },
  {
    name: "Mediator",
    color: "#28a745",
    text:
      "The key distinction is decision vs. advisory. An AI that flags " +
      "statistical outliers for human review, with full transparency of " +
      "methodology and mandatory disclosure to defendants, captures " +
      "consistency benefits while preserving judicial accountability.",
  },
];

export function SwarmDemoPanel({ step }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', width: '100%' }}>
      <div style={{
        padding: '0.5rem 0.75rem', borderRadius: '8px',
        backgroundColor: 'var(--bg-secondary, #f5f5f5)',
        border: '1px solid var(--border-color, #ddd)', fontSize: '0.85rem',
      }}>
        <span style={{
          fontWeight: 600, color: 'var(--text-secondary, #888)',
          fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em',
        }}>
          Topic{' '}
        </span>
        {SWARM_TOPIC}
      </div>
      {SWARM_AGENTS.slice(0, step).map((agent, i) => (
        <Bubble key={i} color={agent.color}>
          <AgentChip name={agent.name} color={agent.color} />
          {agent.text}
        </Bubble>
      ))}
      {step >= 4 && (
        <div className="sdemo-enter" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
          <span style={{
            padding: '0.4rem 0.9rem', borderRadius: '999px', fontSize: '0.82rem',
            fontWeight: 600, border: '1px solid #636cff', color: '#636cff',
            backgroundColor: '#636cff18', cursor: 'default',
          }}>
            ✏️ Intervene
          </span>
          <span style={{
            padding: '0.4rem 0.9rem', borderRadius: '999px', fontSize: '0.82rem',
            fontWeight: 600, border: '1px solid #28a745', color: '#28a745',
            backgroundColor: '#28a74518', cursor: 'default',
          }}>
            📋 Summarize
          </span>
          <span style={{
            fontSize: '0.78rem', color: 'var(--text-secondary, #888)',
            alignSelf: 'center', fontStyle: 'italic',
          }}>
            — steer or conclude the debate at any time
          </span>
        </div>
      )}
      {step >= 5 && (
        <div className="sdemo-enter" style={{
          padding: '0.65rem 0.9rem',
          border: '1px solid #28a745',
          borderRadius: '8px',
          backgroundColor: 'var(--bg-secondary, #f5f5f5)',
          fontSize: '0.82rem',
          lineHeight: 1.55,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            <RiFileTextLine size={15} color="#28a745" style={{ flexShrink: 0 }} />
            <span style={{
              fontSize: '0.7rem', fontWeight: 700, padding: '1px 7px',
              borderRadius: '999px', letterSpacing: '0.03em',
              backgroundColor: 'var(--bg-secondary, #f5f5f5)', color: '#28a745',
              border: '1px solid #28a745',
            }}>
              Summary
            </span>
          </div>
          {SWARM_SUMMARY}
        </div>
      )}
    </div>
  );
}
